import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { db } from '@/lib/firebase';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

import { ysr2Mobile } from '@/features/shared/styles/mobileDesignSystem';

/**
 * Artist-facing support thread (contact_submissions + support_responses).
 * Used in Profile (main /admin app) and legacy Artist Settings view.
 */
const SupportThreadView = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'contact_submissions'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, snap => {
      setSubmissions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!selectedId) {
      setThread([]);
      return;
    }
    const q = query(
      collection(db, 'support_responses'),
      where('submissionId', '==', selectedId),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(q, snap => {
      setThread(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [selectedId]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedId || !user) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'support_responses'), {
        submissionId: selectedId,
        userId: user.uid,
        userEmail: user.email,
        message: replyText.trim(),
        fromAdmin: false,
        createdAt: serverTimestamp(),
        read: false,
      });
      setReplyText('');
      toast({ title: 'Reply sent', description: 'Your message has been sent to the YSR team.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const selected = submissions.find(s => s.id === selectedId);

  const statusColor = (status) =>
    status === 'resolved' ? 'bg-green-600' : status === 'in_progress' ? 'bg-yellow-600' : 'bg-red-600';

  const openMelody = () => {
    window.dispatchEvent(new CustomEvent('openMelody', { detail: {}, bubbles: true }));
  };

  const openMelodyMessage = () => {
    window.dispatchEvent(new CustomEvent('openMelody', { detail: { openContactForm: true }, bubbles: true }));
  };

  if (submissions.length === 0) {
    return (
      <div className="text-center py-6 space-y-4">
        <p className="text-sm text-blue-100/80">No support requests yet.</p>
        <p className="text-sm text-blue-100/65 leading-relaxed px-1">
          If you need help, open <strong className="text-white">More</strong> &gt; <strong className="text-white">Melody</strong>.
          You can read FAQs, or message us directly.
        </p>
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={openMelody}
            className={`${ysr2Mobile.primaryButton} w-full py-3 text-sm font-semibold touch-manipulation`}
          >
            Open Melody
          </button>
          <button
            type="button"
            onClick={openMelodyMessage}
            className={`${ysr2Mobile.secondaryButton} w-full py-3 text-sm font-semibold touch-manipulation`}
          >
            Write a support message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!selectedId && (
        <div className="space-y-2">
          {submissions.map(sub => (
            <button
              key={sub.id}
              type="button"
              onClick={() => setSelectedId(sub.id)}
              className="w-full text-left bg-gray-800 border border-gray-700 rounded-lg p-3 hover:border-purple-500/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-white font-medium text-sm truncate">{sub.subject}</p>
                  <p className="text-gray-400 text-xs mt-0.5">
                    {sub.createdAt?.toDate ? sub.createdAt.toDate().toLocaleDateString() : ''}
                  </p>
                </div>
                <Badge className={`${statusColor(sub.status)} text-white text-xs shrink-0`}>
                  {sub.status === 'in_progress' ? 'In Progress' : sub.status === 'resolved' ? 'Resolved' : 'New'}
                </Badge>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedId && selected && (
        <div className="space-y-3">
          <Button type="button" variant="ghost" size="sm" className="text-gray-400" onClick={() => setSelectedId(null)}>
            ← All requests
          </Button>
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-500">Original message</p>
            <p className="text-sm text-gray-200 mt-1 whitespace-pre-wrap">{selected.message}</p>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {thread.map(msg => (
              <div
                key={msg.id}
                className={`rounded-lg p-3 text-sm ${
                  msg.fromAdmin ? 'bg-purple-900/30 border border-purple-700/40 ml-4' : 'bg-gray-800 border border-gray-600 mr-4'
                }`}
              >
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{msg.fromAdmin ? 'YSR team' : 'You'}</span>
                  <span>{msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleString() : ''}</span>
                </div>
                <p className="text-gray-200 whitespace-pre-wrap">{msg.message}</p>
              </div>
            ))}
          </div>

          {selected.status !== 'resolved' && (
            <div className="space-y-2">
              <Textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Type your reply…"
                className="bg-gray-900 border-gray-600 text-white min-h-[100px]"
              />
              <Button
                type="button"
                onClick={handleSendReply}
                disabled={sending || !replyText.trim()}
                className="bg-purple-600 hover:bg-purple-700"
                size="sm"
              >
                {sending ? 'Sending…' : 'Send Reply'}
              </Button>
            </div>
          )}
          {selected.status === 'resolved' && (
            <p className="text-green-400 text-xs text-center py-2">This request has been resolved.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SupportThreadView;
